import React from 'react';
import PropTypes from 'prop-types';

import Browser from '@wetransfer/concorde-browser';
import Cookie from '@wetransfer/concorde-cookie';
import Router from '@module/router';

import Analytics from '@module/analytics';
import I18n from '@module/i18n';
import { canBeShownAsGrid } from '@module/transfer/preview';

import Assets from '@app/assets';
import Actions from '@app/action';
import Grid from '@app/page/transfer/grid';
import Helper from '@app/helper';
import PreviewHelper from '@app/helper/preview';
import Store from '@app/store';

import Icon from '@component/icon';
import { shouldTriggerAppNotification } from '@component/info/appbar-hoc';
import ItemList from '@component/itemlist';
import Scrollable from '@component/scrollable';
import GridButton from '@component/transfer/downloader/grid-button';
import TransferLink from '@component/transfer-link';
import NotificationPositions from '@constant/notification-positions';
import TransferLinkSources from '@constant/transfer-link-sources';

import { DODGER_BLUE } from '@constant/colors';

const MESSAGE_CUTOFF_POINT = Browser.isMobile ? 170 : 55;

export default class Files extends React.Component {
  static propTypes = {
    transfer: PropTypes.object,
    id: PropTypes.string,
    secret: PropTypes.string,
    recipient: PropTypes.any,
    file_id: PropTypes.string,
    isReady: PropTypes.bool,
  };

  state = {
    isReady: false,
    textExpanded: false,
    showAllFilesOnMobile: false,
    transferLinkButtonTimerId: null,
    transferLinkButtonLabel: I18n.t('transfer_link.buttons.copy'),
  };

  constructor(props) {
    super(props);

    this.onDownloadAll = this.onDownloadAll.bind(this);
    this.onExpandText = this.onExpandText.bind(this);
    this.onFileDownload = this.onFileDownload.bind(this);
    this.onShowAllFiles = this.onShowAllFiles.bind(this);
    this.trackOpenInAppButton = this.trackOpenInAppButton.bind(this);
    this.trackDownloadAppButton = this.trackDownloadAppButton.bind(this);
    this.triggerLinksOnlyTooltip = this.triggerLinksOnlyTooltip.bind(this);
  }

  UNSAFE_componentWillMount() {
    const { route, session } = Store.getState();
    const onGrid = route.path.includes('/grid');
    if (onGrid || !this.previewable) {
      return;
    }

    if (Browser.isMobile) {
      this.mobileGridTimeout = setTimeout(PreviewHelper.goToGrid, 0); // Async
    } else if (!Cookie.get('wt_discover_vrl') && session && session.tandc) {
      Cookie.set('wt_discover_vrl', 1, { days: 365 });
      this.pushTransferNotification = setTimeout(() => {
        Store.dispatch(
          Actions.Notification.pushTransfer({
            text: 'DiscoverVrlTooltip',
            align: '.downloader__grid-button',
          })
        );
      }, 1000);
    }
  }

  componentWillUnmount() {
    const { route } = Store.getState();

    if (Browser.isMobile && route.path.includes('/grid')) {
      const downloadUrl = route.path.split('/grid')[0];
      Router.navigateTo(downloadUrl);
    }
    clearTimeout(this.mobileGridTimeout);
    clearTimeout(this.pushTransferNotification);
  }

  get previewable() {
    return canBeShownAsGrid(this.props.transfer.items);
  }

  /**
   * Notifies the user that links cannot be downloaded
   */
  triggerLinksOnlyTooltip() {
    const {
      notification: {
        transfer: { text },
      },
    } = Store.getState();
    const notificationKey = 'LinksOnlyTransferTooltip';

    const allLinks = this.props.transfer.items.every(
      ({ content_identifier }) => content_identifier === 'web_content'
    );

    if (
      allLinks === false ||
      text === notificationKey ||
      this.props.transfer.state === 'processing'
    ) {
      return;
    }

    Store.dispatch(
      Actions.Notification.pushTransfer({
        text: notificationKey,
        align: '.transfer__window',
      })
    );
  }

  /**
   * If there is a file_id defined, call the scrollToActiveFile method
   */
  componentDidMount() {
    const { file_id } = this.props;

    if (!file_id) {
      return;
    }
    this.scrollToActiveFile();
  }

  /**
   * If the file_id changes, call the scrollToActiveFile method
   *
   * @param {object} prevProps  Components' previous props
   */
  componentDidUpdate(prevProps) {
    if (prevProps.file_id !== this.props.file_id) {
      this.scrollToActiveFile();
    }
  }

  /**
   * If there is a mounted scrollable component, scroll to the active file.
   */
  scrollToActiveFile() {
    if (!this.node) {
      return;
    }

    const previewElem = this.node.querySelector('.filelist__item--previewing');

    if (!previewElem) {
      return;
    }

    if (this.scrollable) {
      if (this.scrollable.isNodeInView(previewElem)) {
        return;
      }
      return this.scrollable.scrollTo(previewElem, {
        align: 'bottom',
        animated: true,
        duration: 250,
      });
    }

    return previewElem.scrollIntoView();
  }

  // We want to download a transfer or single file, so request from the API
  // where we can obtain the file and then go get it!
  async onDownloadAll() {
    await Store.dispatch(
      Actions.Transfer.download(
        this.props.secret,
        this.props.recipient || false
      )
    );
  }

  onExpandText() {
    this.setState({ textExpanded: true });
  }

  onShowAllFiles() {
    this.setState({ showAllFilesOnMobile: true });
  }

  async onFileDownload(id) {
    await Store.dispatch(
      Actions.Transfer.downloadFile(
        this.props.secret,
        this.props.recipient || false,
        id
      )
    );
  }

  renderDownloadIcon(transfer) {
    if (transfer.items.length > 2 && !this.previewable) {
      return null;
    }

    return (
      <Icon
        type={
          transfer.state === 'processing'
            ? 'TransferProcessing'
            : 'TransferReady'
        }
        className="downloader__top-icon"
      />
    );
  }

  trackOpenInApp(action) {
    Analytics.pushDataLayerEvent({
      category: 'popup experiment',
      action: action,
    });
  }

  trackOpenInAppButton = () => {
    this.trackOpenInApp('open in app button');
  };

  trackDownloadAppButton = () => {
    this.trackOpenInApp('download app button');
  };

  renderMobileAppSection() {
    if (!Browser.isIphone && !Browser.isAndroid) {
      return null;
    }

    return (
      <div className="app-introduction">
        <h3>{I18n.t('transfer.appintro.title')}</h3>
        <p>{I18n.t('transfer.collectappintro.description')}</p>
        <a
          onClick={this.trackDownloadAppButton}
          href={Helper.downloadAppUri()}
          title={I18n.t('transfer.appintro.button')}
        >
          {I18n.t('transfer.appintro.button')}
        </a>
        <img
          width="209px"
          height="207px"
          src={Assets.lookup('mobile/app.png')}
          alt=""
        />
      </div>
    );
  }

  onTransferLinkCopied = () => {
    const { transferLinkButtonTimerId } = this.state;

    if (transferLinkButtonTimerId) {
      clearTimeout(transferLinkButtonTimerId);
    }

    const timerId = setTimeout(() => {
      this.setState({
        transferLinkButtonTimerId: null,
        transferLinkButtonLabel: I18n.t('transfer_link.buttons.copy_again'),
      });
    }, 4000);

    this.setState({
      transferLinkButtonTimerId: timerId,
      transferLinkButtonLabel: I18n.t('transfer_link.buttons.copied'),
    });
  };

  // Render our view
  render() {
    const { transfer } = this.props;
    const { transferLinkButtonLabel } = this.state;
    const { route } = Store.getState();
    const { items } = transfer;
    const buttonClassNames = ['transfer__button'];
    const isGrid = this.previewable;
    const hasFiles = transfer.items.some(
      ({ content_identifier }) => content_identifier === 'file'
    );
    let isActiveButton = true;
    let buttonOnMouseEnter = null;
    let downloadLabel = 'download';
    const hasAppbar = shouldTriggerAppNotification(transfer);

    if (transfer.currentFileDownload >= 0 || !this.props.isReady || !hasFiles) {
      isActiveButton = false;
      buttonClassNames.push('transfer__button--inactive');
    }

    if (!hasFiles) {
      buttonOnMouseEnter = this.triggerLinksOnlyTooltip;
    }

    // How long before the transfer expires?
    let description = I18n.t('transfer.download.expires', {
      relative_time: I18n.relativeTime(transfer.expiry_seconds),
    });

    if (typeof transfer.expiry_seconds === 'undefined') {
      description = I18n.t('transfer.download.expires_never');
    }

    if (transfer.isSpaceship) {
      if (transfer.state === 'processing') {
        description = I18n.t('transfer.collection.uploading.copy');
      } else if (!transfer.items.length) {
        description = I18n.t('transfer.collection.empty.copy');
      }
    }

    const headingClassNames = ['downloader__heading'];
    if (!this.previewable) {
      headingClassNames.push(`downloader__heading--${items.length}-files`);
    } else {
      headingClassNames.push('downloader__heading--grid-available');
    }

    let message = null;

    if (transfer.message) {
      let expandButton = null;
      const messageClassNames = ['downloader__message'];
      let textMessage = transfer.message;
      headingClassNames.push('downloader__heading--message');

      // Cut it off.
      if (
        transfer.message.length < MESSAGE_CUTOFF_POINT ||
        this.state.textExpanded
      ) {
        messageClassNames.push('downloader__message--expanded');
      } else if (transfer.message.length >= MESSAGE_CUTOFF_POINT) {
        textMessage = `${textMessage.substr(0, MESSAGE_CUTOFF_POINT)} ...`;
        expandButton = (
          <span className="downloader__expand-msg" onClick={this.onExpandText}>
            {I18n.t('transfer.download.expand')}
            <Icon type="Arrow" orientation="bottom" />
          </span>
        );
      }

      message = (
        <div className={messageClassNames.join(' ')}>
          <p>
            {textMessage}
            {expandButton}
          </p>
        </div>
      );
    }

    let title = transfer.title || I18n.t('transfer.download.title');
    if (!this.props.isReady) {
      title = I18n.t('transfer.pending.title');
    }

    let itemList = null;

    if (isGrid) {
      itemList = (
        <GridButton
          isPanelOpen={
            route.name === 'grid-panel' || route.name === 'preview-panel'
          }
          ready={this.props.isReady}
          transfer={transfer}
        />
      );
    } else {
      itemList = (
        <ItemList
          transfer={transfer}
          altText={I18n.t('transfer.pending.subtitle')}
          showPending={!this.props.isReady}
          maxFiles={
            !Browser.isMobile || this.state.showAllFilesOnMobile ? 0 : 5
          }
          previewable={
            transfer.items.find((item) => item.previewable === true) !==
            undefined
          }
          onDownload={this.onFileDownload}
          id={this.props.id}
          secret={this.props.secret}
          recipient={this.props.recipient}
          file_id={this.props.file_id}
        />
      );
    }

    if (hasAppbar) {
      headingClassNames.push('downloader__heading--popup-under-navbar');
    }

    let content = (
      <Scrollable
        ref={(node) => (this.scrollable = node)}
        className="transfer__contents"
      >
        <div className={headingClassNames.join(' ')}>
          {this.renderDownloadIcon(transfer)}
          <h2>{title}</h2>
          <p>{description}</p>
          {message}
        </div>
        {itemList}
      </Scrollable>
    );

    if (Browser.isMobile) {
      if (items.length > 1) {
        downloadLabel = 'download_all_files';
      }

      content = [
        <div key="render_download" className={headingClassNames.join(' ')}>
          {this.renderDownloadIcon(transfer)}
          <h2>{title}</h2>
          <p>{description}</p>
          {message}
        </div>,
      ];

      if (isGrid) {
        content.push(<Grid key="grid" items={items} route={route} />);
      } else {
        content.push(
          <h3
            key="downloader__file-count-title"
            className="downloader__file-count-title"
          >
            {I18n.t('transfer.download.files_nosize', {
              count: transfer.items.length,
            })}
          </h3>
        );
        content.push(itemList);
        if (!this.state.showAllFilesOnMobile && items.length > 5) {
          content.push(
            <span
              key="downloader__show-all-files"
              className="downloader__show-all-files"
              onClick={this.onShowAllFiles}
            >
              {I18n.t('transfer.download.show_all_files_mobile')}
            </span>
          );
        }
      }

      if (transfer.shortURL) {
        content.push(
          <TransferLink
            key="transfer-link"
            label={I18n.t('transfer_link.labels.share')}
            buttonLabel={transferLinkButtonLabel}
            notificationPosition={NotificationPositions.none}
            className="u-margin-top u-margin-bottom"
            queryParams={{ src: TransferLinkSources.downloads }}
            onLinkCopied={this.onTransferLinkCopied}
          />
        );
      }
    }

    const footerContents = [];

    //Checking conditions to show VRL preview button
    let buttonAction = this.onDownloadAll;
    let downloadString = I18n.t(`transfer.buttons.${downloadLabel}`);
    const variant = PreviewHelper.getVariantForPreviewButtonExperiment({
      isMobile: Browser.isMobile,
    });

    if (variant === true) {
      buttonAction = PreviewHelper.goToGrid;
      downloadString = I18n.t('page.advertise.gallery.preview');
      const previewIcon = (
        <span
          className="downloader__grid-button-preview_2"
          onClick={this.onDownloadAll}
        >
          <Icon type="DownloadSmall" color={DODGER_BLUE} />
        </span>
      );
      footerContents.push(previewIcon);
    }

    if (hasAppbar) {
      downloadLabel = items.length === 1 ? 'download_file' : 'download_zip';
      downloadString = I18n.t(`transfer.buttons.${downloadLabel}`);

      const mobileDownloadContent = [
        <a
          onClick={this.trackOpenInAppButton}
          href={Helper.openInAppUri(transfer, route)}
          key="transfer_footer_openinapp"
          className="button button--enabled"
        >
          {I18n.t('transfer.buttons.openincollectapp')}
        </a>,
        <button
          key="transfer_footer_action"
          onClick={buttonAction}
          className={buttonClassNames.join(' ')}
          disabled={!isActiveButton}
        >
          {downloadString}
        </button>,
      ];
      footerContents.push(mobileDownloadContent);
    } else {
      footerContents.push(
        <button
          key="transfer_footer_action"
          onClick={buttonAction}
          onMouseEnter={buttonOnMouseEnter}
          className={buttonClassNames.join(' ')}
          disabled={!isActiveButton}
        >
          {downloadString}
        </button>
      );
    }

    if (Browser.isMobile && items.length > 1) {
      footerContents.push(
        <p
          key="zip_information"
          dangerouslySetInnerHTML={{
            __html: I18n.t('transfer.download.zip_information'),
          }}
        />
      );
    }

    const classNames = ['transfer__window', 'downloader'];
    const footerClassNames = ['transfer__footer'];

    if (hasAppbar) {
      footerClassNames.push('transfer__footer--popup-under-navbar');
    }

    // TODO: Info icon based on X hours remaining
    return (
      <div ref={(node) => (this.node = node)} className={classNames.join(' ')}>
        {content}
        <div className={footerClassNames.join(' ')}>{footerContents}</div>
        {this.renderMobileAppSection()}
      </div>
    );
  }
}
